import type { GameEvent } from "@synapse/shared";

/** An event as the client builds it; track() adds the player id. */
export type TrackedEvent = Omit<GameEvent, "player_id">;

const MAX_BUFFER_SIZE = 50;

export class TelemetryService {
  private buffer: GameEvent[] = [];
  private apiBase: string;
  private playerId: string;
  // Off until the player opts in (the consent UI calls setEnabled(true)).
  private enabled = false;

  constructor(apiBase: string, playerId: string) {
    this.apiBase = apiBase;
    this.playerId = playerId;
  }

  get bufferSize(): number {
    return this.buffer.length;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.buffer = [];
    }
  }

  track(event: TrackedEvent): void {
    if (!this.enabled) return;

    this.buffer.push({ ...event, player_id: this.playerId });

    // Cap buffer size — drop oldest events
    if (this.buffer.length > MAX_BUFFER_SIZE) {
      this.buffer = this.buffer.slice(-MAX_BUFFER_SIZE);
    }
  }

  async flush(): Promise<void> {
    if (!this.enabled || this.buffer.length === 0) return;

    const batch = [...this.buffer];

    try {
      const res = await fetch(`${this.apiBase}/api/v1/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ events: batch }),
      });

      // Sent, or rejected with a 4xx (retrying would be rejected again):
      // drop the batch. A 5xx keeps it for the next flush.
      if (res.ok || (res.status >= 400 && res.status < 500)) {
        this.buffer = this.buffer.slice(batch.length);
      }
    } catch {
      // Network failure — keep events in buffer for retry
    }
  }
}
